import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// ─── Thème PESACORE (identique au LandingScreen) ──────────────────────────────
const T = {
  bg:         "#0A0A0F",
  surface:    "#0f172a",
  surfaceAlt: "#0d1526",
  surfaceHov: "#162035",
  text:       "#f8fafc",
  textSub:    "#e2e8f0",
  textMuted:  "#c7c7c7",
  textFaint:  "#cecece",
  border:     "rgba(255,255,255,0.06)",
  borderMed:  "rgba(255,255,255,0.10)",
  orange:     "#fdba74",
  orangeDim:  "rgba(253,186,116,0.08)",
  sky:        "#7dd3fc",
  skyDim:     "rgba(125,211,252,0.08)",
  amber:      "#fcd34d",
  amberDim:   "rgba(252,211,77,0.08)",
  emerald:    "#6ee7b7",
  danger:     "#fca5a5",
};

const SECTIONS = [
  { id: "responsable",   label: "Responsable de traitement", num: "01" },
  { id: "collecte",      label: "Données collectées",        num: "02" },
  { id: "finalites",     label: "Finalités du traitement",   num: "03" },
  { id: "base-legale",   label: "Base légale",               num: "04" },
  { id: "destinataires", label: "Destinataires",             num: "05" },
  { id: "conservation",  label: "Durée de conservation",     num: "06" },
  { id: "droits",        label: "Vos droits RGPD",           num: "07" },
  { id: "transferts",    label: "Transferts hors UE",        num: "08" },
  { id: "cookies",       label: "Cookies et traceurs",       num: "09" },
  { id: "modifications", label: "Modifications",             num: "10" },
];

function Tag({ children, color = T.orange }) {
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: "3px",
      background: `${color}10`,
      border: `1px solid ${color}28`,
      color,
      fontSize: "10px",
      fontWeight: 700,
      fontFamily: "'JetBrains Mono', monospace",
      letterSpacing: "0.05em",
    }}>
      {children}
    </span>
  );
}

function SectionBlock({ id, num, title, children }) {
  return (
    <div
      id={id}
      style={{
        padding: "32px 0",
        borderBottom: `1px solid ${T.border}`,
        scrollMarginTop: "80px",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "20px", marginBottom: "20px" }}>
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "11px",
          fontWeight: 700,
          color: T.textFaint,
          minWidth: "28px",
          paddingTop: "4px",
        }}>
          {num}
        </span>
        <h2 style={{
          fontSize: "18px",
          fontWeight: 700,
          color: T.text,
          fontFamily: "'Inter', sans-serif",
          letterSpacing: "-0.01em",
          lineHeight: 1.3,
        }}>
          {title}
        </h2>
      </div>
      <div style={{ paddingLeft: "48px" }}>
        {children}
      </div>
    </div>
  );
}

function DataRow({ label, value, accent = false }) {
  return (
    <div style={{
      display: "flex",
      gap: "16px",
      padding: "10px 14px",
      borderRadius: "4px",
      background: accent ? T.orangeDim : "transparent",
      border: `1px solid ${accent ? `${T.orange}20` : T.border}`,
      marginBottom: "6px",
      alignItems: "flex-start",
    }}>
      <span style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: "10px",
        fontWeight: 600,
        color: accent ? T.orange : T.textFaint,
        minWidth: "160px",
        flexShrink: 0,
        paddingTop: "2px",
        letterSpacing: "0.02em",
      }}>
        {label}
      </span>
      <span style={{
        fontSize: "13px",
        color: T.textMuted,
        lineHeight: 1.65,
        fontFamily: "'Inter', sans-serif",
      }}>
        {value}
      </span>
    </div>
  );
}

function BulletList({ items, color = T.textMuted }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "8px" }}>
      {items.map((item, i) => (
        <li key={i} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
          <span style={{
            color: T.orange,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "10px",
            flexShrink: 0,
            paddingTop: "4px",
          }}>
            ▸
          </span>
          <span style={{ fontSize: "13px", color, lineHeight: 1.7, fontFamily: "'Inter', sans-serif" }}>
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function RightBadge({ children, color = T.emerald }) {
  return (
    <div style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 14px",
      borderRadius: "4px",
      background: `${color}08`,
      border: `1px solid ${color}22`,
      marginBottom: "16px",
    }}>
      <div style={{ width: 6, height: 6, borderRadius: "50%", background: color }} />
      <span style={{
        fontSize: "11px",
        fontWeight: 600,
        color,
        fontFamily: "'JetBrains Mono', monospace",
        letterSpacing: "0.04em",
      }}>
        {children}
      </span>
    </div>
  );
}

export default function PolitiqueConfidentialite({ onBack, isDark = true }) {
  const [activeSection, setActiveSection] = useState("responsable");
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();
  const observer = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    observer.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveSection(e.target.id);
        });
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.current.observe(el);
    });
    return () => observer.current?.disconnect();
  }, [visible]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: T.bg,
      fontFamily: "'Inter', 'JetBrains Mono', monospace",
      color: T.text,
      position: "relative",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,300;14..32,400;14..32,500;14..32,600;14..32,700;14..32,800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes pulse { 0%,100%{opacity:.4} 50%{opacity:1} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        .pc-nav-item { transition: all 0.15s; cursor: pointer; }
        .pc-nav-item:hover { color: #fdba74 !important; }
        .pc-placeholder {
          display: inline-block;
          background: rgba(253,186,116,0.08);
          border: 1px dashed rgba(253,186,116,0.35);
          border-radius: 3px;
          padding: 1px 8px;
          color: #fdba74;
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          letter-spacing: 0.03em;
        }
      `}</style>

      {/* Grille fond */}
      <div style={{
        position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
        backgroundImage: `linear-gradient(to right,rgba(34,211,238,0.03) 1px,transparent 1px),linear-gradient(to bottom,rgba(245,158,11,0.03) 1px,transparent 1px)`,
        backgroundSize: "72px 72px",
        maskImage: "radial-gradient(ellipse at 50% 0%,black 30%,transparent 70%)",
      }} />

      {/* Header */}
      <header style={{
        height: "56px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 24px",
        borderBottom: `1px solid ${T.border}`,
        background: "rgba(10,10,15,0.92)",
        backdropFilter: "blur(12px)",
        position: "sticky",
        top: 0,
        zIndex: 100,
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "none", border: `1px solid ${T.border}`,
            cursor: "pointer", color: T.textFaint,
            padding: "5px 12px", borderRadius: "4px",
            fontSize: "11px", fontWeight: 500,
            transition: "all 0.2s", fontFamily: "'JetBrains Mono', monospace",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.orange; e.currentTarget.style.color = T.orange; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textFaint; }}
        >
          ← retour
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: "8px",
            padding: "4px 12px",
            background: T.surfaceAlt,
            borderRadius: "4px", border: `1px solid ${T.border}`,
          }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#10B981", animation: "pulse 2s infinite" }} />
            <span style={{ fontSize: "11px", fontWeight: 600, color: T.textFaint, letterSpacing: "0.05em" }}>
              PESACORE · LÉGAL
            </span>
          </div>
          <div style={{ width: 1, height: 28, background: T.border }} />
          <span style={{ fontSize: "11px", color: T.textFaint }}>v1.0</span>
        </div>

        <Tag>RGPD · 09 AVRIL 2026</Tag>
      </header>

      {/* Layout : sidebar + contenu */}
      <div style={{
        maxWidth: "1100px",
        margin: "0 auto",
        padding: "0 24px",
        display: "flex",
        gap: "48px",
        position: "relative",
        zIndex: 5,
      }}>

        {/* Sidebar navigation */}
        <aside style={{
          width: "220px",
          flexShrink: 0,
          position: "sticky",
          top: "80px",
          alignSelf: "flex-start",
          paddingTop: "48px",
          paddingBottom: "48px",
        }}>
          <div style={{ marginBottom: "20px" }}>
            <span style={{
              fontSize: "10px", fontWeight: 700, color: T.textFaint,
              fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.07em",
            }}>
              SOMMAIRE
            </span>
          </div>
          <nav style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {SECTIONS.map(({ id, label, num }) => {
              const isActive = activeSection === id;
              return (
                <div
                  key={id}
                  className="pc-nav-item"
                  onClick={() => scrollTo(id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "7px 10px",
                    borderRadius: "4px",
                    borderLeft: `2px solid ${isActive ? T.orange : "transparent"}`,
                    background: isActive ? T.orangeDim : "transparent",
                    color: isActive ? T.orange : T.textFaint,
                    fontSize: "12px",
                    fontFamily: "'JetBrains Mono', monospace",
                    transition: "all 0.15s",
                  }}
                >
                  <span style={{ fontSize: "9px", opacity: 0.6 }}>{num}</span>
                  <span style={{ fontSize: "11px", lineHeight: 1.4 }}>{label}</span>
                </div>
              );
            })}
          </nav>

          {/* Bloc légal bas */}
          <div style={{
            marginTop: "32px",
            padding: "12px 14px",
            borderRadius: "4px",
            background: T.surfaceAlt,
            border: `1px solid ${T.border}`,
          }}>
            <span style={{ fontSize: "10px", color: T.textFaint, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.7, display: "block" }}>
              ⓘ Ce document constitue<br />
              la politique de confidentialité<br />
              au sens du RGPD.
            </span>
          </div>
        </aside>

        {/* Contenu principal */}
        <main style={{
          flex: 1,
          paddingTop: "48px",
          paddingBottom: "80px",
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 0.55s ease, transform 0.55s ease",
        }}>
          {/* Hero du document */}
          <div style={{ marginBottom: "8px" }}>
            <div style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              padding: "5px 14px",
              borderRadius: "4px",
              background: T.orangeDim,
              border: `1px solid ${T.orange}25`,
              marginBottom: "24px",
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              <span style={{ fontSize: "11px", fontWeight: 700, color: T.orange, letterSpacing: "0.08em" }}>
                ▸ POLITIQUE DE CONFIDENTIALITÉ
              </span>
              <span style={{ fontSize: "10px", color: T.orange, animation: "pulse 2s infinite" }}>●</span>
            </div>

            <h1 style={{
              fontSize: "clamp(28px, 3.5vw, 38px)",
              fontWeight: 700,
              color: T.text,
              lineHeight: 1.15,
              letterSpacing: "-0.025em",
              fontFamily: "'Inter', sans-serif",
              marginBottom: "16px",
            }}>
              Protection de<br />
              <span style={{ color: T.orange }}>vos données personnelles</span>
            </h1>

            <p style={{
              fontSize: "14px",
              color: T.textMuted,
              lineHeight: 1.75,
              maxWidth: "560px",
              fontStyle: "italic",
              fontFamily: "'Inter', sans-serif",
              marginBottom: "24px",
            }}>
              PESACORE Architect s'engage à protéger vos données personnelles conformément au RGPD et à la loi Informatique et Libertés.
            </p>

            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "8px" }}>
              <Tag>RGPD</Tag>
              <Tag color={T.sky}>Loi Informatique et Libertés</Tag>
              <Tag color={T.emerald}>Serveurs en France · LWS</Tag>
            </div>
          </div>

          <div style={{ height: "1px", background: T.border, margin: "32px 0" }} />

          {/* ── Section 01 ── */}
          <SectionBlock id="responsable" num="01" title="Responsable de traitement">
            <p style={{ fontSize: "13px", color: T.textMuted, lineHeight: 1.7, marginBottom: "16px", fontFamily: "'Inter', sans-serif" }}>
              Le responsable de traitement au sens du RGPD est :
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <DataRow label="Entité" value="PESACORE Architect" accent />
              <DataRow label="Forme juridique / SIREN" value={<span className="pc-placeholder">SAS / 104 973 771 R.C.S. Marseille</span>} />
              <DataRow label="Adresse" value={<span className="pc-placeholder">55 rue Grignan, 13006 Marseille</span>} />
              <DataRow label="Contact RGPD / DPO" value={<span className="pc-placeholder">sécurité : admin@pesacore.fr</span>} />
            </div>
          </SectionBlock>

          {/* ── Section 02 ── */}
          <SectionBlock id="collecte" num="02" title="Données collectées">
            <p style={{ fontSize: "13px", color: T.textMuted, lineHeight: 1.7, marginBottom: "16px", fontFamily: "'Inter', sans-serif" }}>
              Dans le cadre de la fourniture du Service, nous collectons les catégories de données suivantes :
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <DataRow label="Identification" value="Nom, prénom, email, mot de passe (hashé)" />
              <DataRow label="Connexion & usage" value="Adresses IP, logs de connexion, dashboards consultés, traces d'activité" />
              <DataRow label="Contenu Utilisateur" value="Traces décisionnelles, simulations causales, dossiers, bifurcations créés via le ParcoursCreator, CaseCreator, AtelierCreator, CommercialCreator ou les dashboards" />
              <DataRow label="Paiement" value="Gérées par Stripe/PayPal — nous ne stockons aucun numéro de carte bancaire" accent />
              <DataRow label="Données organisationnelles" value="Pour les plans Academia/Entreprise : liste des utilisateurs, cohortes GrandeÉcole, statistiques d'usage" />
            </div>
          </SectionBlock>

          {/* ── Section 03 ── */}
          <SectionBlock id="finalites" num="03" title="Finalités du traitement">
            <RightBadge color={T.sky}>5 finalités identifiées</RightBadge>
            <BulletList items={[
              "Fournir et améliorer le Service (accès aux dashboards apprenant et admin, simulations, traces décisionnelles)",
              "Gérer les abonnements et la facturation",
              "Assurer la sécurité du Service et prévenir la fraude",
              "Analyser l'usage du Service (analytics anonymisés uniquement)",
              "Communiquer avec vous : support, mises à jour du Service, newsletter (avec consentement préalable)",
            ]} />
          </SectionBlock>

          {/* ── Section 04 ── */}
          <SectionBlock id="base-legale" num="04" title="Base légale">
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {[
                { base: "Exécution du contrat", detail: "Traitements nécessaires à la fourniture du Service (CGU/CGV)", color: T.orange },
                { base: "Consentement", detail: "Communications marketing et newsletter — révocable à tout moment", color: T.sky },
                { base: "Intérêt légitime", detail: "Amélioration du Service, sécurité informatique et prévention de la fraude", color: T.emerald },
              ].map(({ base, detail, color }) => (
                <div key={base} style={{
                  display: "flex", gap: "14px", padding: "12px 14px",
                  borderRadius: "4px", background: `${color}06`,
                  border: `1px solid ${color}18`,
                  alignItems: "flex-start",
                }}>
                  <span style={{
                    width: "10px", height: "10px", borderRadius: "50%",
                    background: color, flexShrink: 0, marginTop: "3px",
                  }} />
                  <div>
                    <div style={{ fontSize: "12px", fontWeight: 700, color, fontFamily: "'JetBrains Mono', monospace", marginBottom: "4px", letterSpacing: "0.02em" }}>
                      {base}
                    </div>
                    <div style={{ fontSize: "13px", color: T.textMuted, lineHeight: 1.65, fontFamily: "'Inter', sans-serif" }}>
                      {detail}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </SectionBlock>

          {/* ── Section 05 ── */}
          <SectionBlock id="destinataires" num="05" title="Destinataires des données">
            <BulletList items={[
              "Nos sous-traiteurs techniques : hébergeur LWS (serveurs en France), prestataires de paiement (Stripe/PayPal)",
              "Pour les plans organisationnels : l'administrateur Commandement de votre organisation dans le cadre du dashboard admin",
            ]} />
            <div style={{
              marginTop: "16px",
              padding: "12px 14px",
              borderRadius: "4px",
              background: `${T.emerald}08`,
              border: `1px solid ${T.emerald}20`,
              display: "flex", gap: "10px", alignItems: "flex-start",
            }}>
              <span style={{ color: T.emerald, flexShrink: 0, marginTop: "1px", fontSize: "13px" }}>✓</span>
              <span style={{ fontSize: "13px", color: T.textMuted, lineHeight: 1.65, fontFamily: "'Inter', sans-serif" }}>
                <strong style={{ color: T.emerald, fontWeight: 600 }}>Aucune vente de données à des tiers.</strong> Vos données ne sont jamais cédées ou monétisées auprès de partenaires commerciaux.
              </span>
            </div>
          </SectionBlock>

          {/* ── Section 06 ── */}
          <SectionBlock id="conservation" num="06" title="Durée de conservation">
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <DataRow label="Données de compte" value="Pendant la durée d'utilisation du Service + 3 ans après résiliation" />
              <DataRow label="Contenu Utilisateur" value="Pendant la durée du contrat + 1 an (sauf demande de suppression anticipée)" />
              <DataRow label="Données de paiement" value="Durée légale : 10 ans (obligations comptables)" accent />
            </div>
          </SectionBlock>

          {/* ── Section 07 ── */}
          <SectionBlock id="droits" num="07" title="Vos droits (RGPD)">
            <p style={{ fontSize: "13px", color: T.textMuted, lineHeight: 1.7, marginBottom: "16px", fontFamily: "'Inter', sans-serif" }}>
              Conformément au RGPD (articles 15 à 22), vous disposez des droits suivants sur vos données :
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "20px" }}>
              {[
                { right: "Accès", desc: "Obtenir une copie de vos données" },
                { right: "Rectification", desc: "Corriger des données inexactes" },
                { right: "Effacement", desc: "Demander la suppression" },
                { right: "Limitation", desc: "Restreindre un traitement" },
                { right: "Portabilité", desc: "Recevoir vos données dans un format structuré" },
                { right: "Opposition", desc: "Vous opposer à un traitement fondé sur l'intérêt légitime" },
              ].map(({ right, desc }) => (
                <div key={right} style={{
                  padding: "10px 12px",
                  borderRadius: "4px",
                  background: T.surfaceAlt,
                  border: `1px solid ${T.border}`,
                }}>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: T.orange, fontFamily: "'JetBrains Mono', monospace", marginBottom: "4px", letterSpacing: "0.03em" }}>
                    {right}
                  </div>
                  <div style={{ fontSize: "12px", color: T.textMuted, lineHeight: 1.55, fontFamily: "'Inter', sans-serif" }}>
                    {desc}
                  </div>
                </div>
              ))}
            </div>
            <DataRow label="Exercer vos droits" value={<span className="pc-placeholder">admin@pesacore.fr</span>} accent />
            <div style={{ marginTop: "12px" }}>
              <p style={{ fontSize: "13px", color: T.textMuted, lineHeight: 1.7, fontFamily: "'Inter', sans-serif" }}>
                Vous pouvez également introduire une réclamation auprès de la{" "}
                <span style={{ color: T.sky, fontWeight: 500 }}>CNIL</span>{" "}
                (Commission Nationale de l'Informatique et des Libertés) sur{" "}
                <span style={{ color: T.sky }}>cnil.fr</span>.
              </p>
            </div>
          </SectionBlock>

          {/* ── Section 08 ── */}
          <SectionBlock id="transferts" num="08" title="Transferts hors UE">
            <RightBadge color={T.emerald}>Aucun transfert hors Union Européenne</RightBadge>
            <p style={{ fontSize: "13px", color: T.textMuted, lineHeight: 1.7, fontFamily: "'Inter', sans-serif" }}>
              L'ensemble de vos données est hébergé sur les serveurs de{" "}
              <span style={{ color: T.text, fontWeight: 500 }}>LWS (Ligne Web Services)</span>,
              société française dont les infrastructure sont localisées en France.
              Aucun transfert de données vers des pays tiers n'est réalisé.
            </p>
          </SectionBlock>

          {/* ── Section 09 ── */}
          <SectionBlock id="cookies" num="09" title="Cookies et traceurs">
            <p style={{ fontSize: "13px", color: T.textMuted, lineHeight: 1.7, marginBottom: "16px", fontFamily: "'Inter', sans-serif" }}>
              Nous utilisons uniquement des cookies <strong style={{ color: T.text, fontWeight: 500 }}>techniques et indispensables</strong> au fonctionnement du Service (gestion de session, authentification, dashboards). Aucun cookie publicitaire ou de tracking tiers n'est déposé.
            </p>
            <BulletList items={[
              "Cookie de session : maintien de votre connexion",
              "Cookie de préférences : mémorisation de vos paramètres d'interface (thème, langue)",
              "Gestion via les paramètres de votre navigateur — la désactivation peut altérer le fonctionnement du Service",
            ]} />
          </SectionBlock>

          {/* ── Section 10 ── */}
          <SectionBlock id="modifications" num="10" title="Modifications">
            <p style={{ fontSize: "13px", color: T.textMuted, lineHeight: 1.7, fontFamily: "'Inter', sans-serif" }}>
              Toute modification substantielle de cette Politique de Confidentialité sera notifiée par email ou via une notification dans le Service, avec un délai raisonnable avant entrée en vigueur.
              La version en vigueur est toujours accessible sur{" "}
              <span style={{ color: T.sky }}>pesacore.fr/confidentialite</span>.
            </p>
          </SectionBlock>

          {/* Footer du document */}
          <div style={{
            marginTop: "48px",
            padding: "20px 24px",
            borderRadius: "6px",
            background: T.surfaceAlt,
            border: `1px solid ${T.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            <span style={{ fontSize: "11px", color: T.textFaint }}>
              dernière mise à jour : 09 avril 2026
            </span>
            <span style={{ fontSize: "11px", color: T.textFaint }}>
              PESACORE Architect — tous droits réservés
            </span>
          </div>
        </main>
      </div>
    </div>
  );
}